import { supabase } from "@/integrations/supabase/client";
import { toNameInitials, truncateNotificationMessage } from "@/lib/notificationMessages/formatters";
import { logOperationalError } from "@/lib/operationalLogger";

/**
 * Envia push notification para todos os usuários inscritos.
 */
async function sendPushNotification(params: {
  title: string;
  body: string;
  exclude_user_id?: string;
  tag?: string;
  url?: string;
  target_role?: string;
  target_user_ids?: string[];
}) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) return;

    const { error } = await supabase.functions.invoke("send-push", {
      body: params,
    });
    if (error) {
      logOperationalError("notification.send_push.invoke_failed", error, { params });
    }
  } catch (err) {
    logOperationalError("notification.send_push.unexpected_error", err, { params });
  }
}

/**
 * Tipos de notificação disponíveis para envio por e-mail e sino do sistema.
 */
const SYSTEM_NOTIFICATION_EXECUTOR = "Serafisio";

const NOTIFICATION_TYPES = [
  { key: "login", label: "Login de usuário", description: "Quando alguém entra no sistema", targetRole: "master" },
  { key: "logout", label: "Logout de usuário", description: "Quando alguém sai do sistema", targetRole: "master" },
  { key: "agendamento_criado", label: "Novo agendamento", description: "Quando um agendamento é criado", targetRole: "all" },
  { key: "agendamento_cancelado", label: "Agendamento cancelado", description: "Quando um agendamento é cancelado", targetRole: "all" },
  { key: "agendamento_editado", label: "Agendamento editado", description: "Quando um agendamento é reagendado", targetRole: "all" },
  { key: "agendamento_status_alterado", label: "Status do agendamento alterado", description: "Quando o status de um agendamento é atualizado", targetRole: "all" },
  { key: "estoque_baixo", label: "Estoque baixo", description: "Quando um item atinge o mínimo", targetRole: "master" },
  { key: "paciente_cadastrado", label: "Novo paciente", description: "Quando um paciente é cadastrado", targetRole: "master" },
  { key: "pagamento_registrado", label: "Pagamento registrado", description: "Quando um pagamento de paciente é registrado", targetRole: "master" },
  { key: "avaliacao_fisio", label: "Avaliação criada", description: "Quando uma avaliação é registrada", targetRole: "all" },
  { key: "mensagem_chat", label: "Mensagem no chat", description: "Quando uma nova mensagem é recebida no chat", targetRole: "all" },
  { key: "pacote_proximo_fim", label: "Pacote próximo do fim", description: "Quando um pacote de sessões atinge o limite configurado em Alertas", targetRole: "all" },
  { key: "mensalidade_proxima_fim", label: "Mensalidade próxima do fim", description: "Quando uma mensalidade atinge o limite de sessões configurado em Alertas", targetRole: "all" },
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number]["key"];


export const UNIQUE_NOTIFICATION_TYPES = Array.from(
  new Map(NOTIFICATION_TYPES.map((notificationType) => [notificationType.key, notificationType])).values()
);


/** Returns the target_role for a given notification type */
function getNotificationTargetRole(type: NotificationType): string {
  return UNIQUE_NOTIFICATION_TYPES.find((nt) => nt.key === type)?.targetRole ?? "master";
}

async function resolveExecutorName(actorUserId?: string, explicitExecutorName?: string) {
  if (explicitExecutorName?.trim()) return explicitExecutorName.trim();
  if (!actorUserId) return SYSTEM_NOTIFICATION_EXECUTOR;

  const profileByUserIdResponse = await supabase
    .from("profiles")
    .select("full_name, name, nickname, email")
    .eq("user_id", actorUserId)
    .limit(1)
    .maybeSingle();

  const shouldFallbackToProfileId =
    (!profileByUserIdResponse.data && !profileByUserIdResponse.error) ||
    profileByUserIdResponse.error?.code === "42703";

  const profileFallbackById = shouldFallbackToProfileId
    ? await supabase
        .from("profiles")
        .select("full_name, name, nickname, email")
        .eq("id", actorUserId)
        .limit(1)
        .maybeSingle()
    : profileByUserIdResponse;

  const profile = profileFallbackById.data as
    | { full_name?: string | null; name?: string | null; nickname?: string | null; email?: string | null }
    | null;

  return (
    profile?.nickname?.trim() ||
    profile?.full_name?.trim() ||
    profile?.name?.trim() ||
    profile?.email?.trim() ||
    SYSTEM_NOTIFICATION_EXECUTOR
  );
}

/**
 * Dispara e-mail de notificação para usuários MASTER (se habilitado nas preferências).
 */
export async function sendNotificationEmail(params: {
  notification_type: NotificationType;
  message: string;
  action?: string;
  target_role?: string;
}) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) return;

    const { error } = await supabase.functions.invoke("send-notification-email", {
      body: params,
    });
    if (error) {
      logOperationalError("notification.send_email.invoke_failed", error, { params });
    }
  } catch (err) {
    // Falha no envio de e-mail não deve bloquear a operação principal
    logOperationalError("notification.send_email.unexpected_error", err, { params });
  }
}

/**
 * Cria uma notificação no sistema (sino) E dispara e-mail (se habilitado).
 * Unifica ambos os canais em uma única chamada.
 */
export async function sendSystemNotification(params: {
  notification_type: NotificationType;
  message: string;
  action?: string;
  executor_name?: string;
  actor_user_id?: string;
  target_user_ids?: string[];
}) {
  const executorName = await resolveExecutorName(params.actor_user_id, params.executor_name);
  const formattedMessage = params.message.trim();
  const targetRole = getNotificationTargetRole(params.notification_type);

  // 1. Inserir na tabela notifications (sino)
  try {
    const baseRow = {
      type: params.notification_type,
      message: formattedMessage,
      action: params.action ?? null,
      executor_name: executorName,
      actor_user_id: params.actor_user_id ?? null,
      read: false,
      event_at: new Date().toISOString(),
      target_role: targetRole,
    };

    // When there are specific target users, create one notification row per
    // recipient so each standard user can see notifications directed at them.
    const targetIds = params.target_user_ids?.filter(Boolean) ?? [];
    if (targetIds.length > 0) {
      const rows = targetIds.map((uid) => ({ ...baseRow, target_user_id: uid }));
      const { error } = await supabase.from("notifications").insert(rows);
      if (error) logOperationalError("notification.system.insert_targeted_failed", error, { params, targetIdsCount: targetIds.length });
    } else {
      const { error } = await supabase.from("notifications").insert(baseRow);
      if (error) logOperationalError("notification.system.insert_failed", error, { params });
    }
  } catch (err) {
    logOperationalError("notification.system.unexpected_error", err, { params });
  }

  // 2. Disparar e-mail de notificação (assíncrono, não bloqueia)
  sendNotificationEmail({
    notification_type: params.notification_type,
    message: formattedMessage,
    action: params.action,
    target_role: targetRole,
  });

  // 3. Disparar push notification (assíncrono, não bloqueia)
  const isChatMessage = params.notification_type === "mensagem_chat";
  const safePushPatientMessage = [
    "agendamento_criado",
    "agendamento_status_alterado",
    "agendamento_editado",
    "agendamento_cancelado",
    "paciente_cadastrado",
  ].includes(params.notification_type)
    ? toNameInitials(formattedMessage.split(" — ")[0])
    : null;

  const pushTitle = executorName !== SYSTEM_NOTIFICATION_EXECUTOR ? executorName : SYSTEM_NOTIFICATION_EXECUTOR;
  const chatPushAction = `${executorName} enviou uma mensagem`;
  const pushBody = isChatMessage
    ? `${chatPushAction}: ${truncateNotificationMessage(formattedMessage || params.message)}`
    : formattedMessage
      ? `${params.action || "Atualização"}: ${safePushPatientMessage ?? formattedMessage}`
      : (params.action || "Você tem uma nova atualização");

  sendPushNotification({
    title: pushTitle,
    body: pushBody,
    exclude_user_id: params.actor_user_id,
    tag: params.notification_type,
    url: isChatMessage ? "/chat" : "/",
    target_role: targetRole,
    target_user_ids: params.target_user_ids,
  });
}
